import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Badge, Box, ListItemText, Tooltip, Typography } from "@mui/material";

import { NavigationLabelType } from "../../../../Navigation.types";

interface Props {
  label: NavigationLabelType;
  count?: number;
}

export function NavigationItemLabel({ label, count }: Props) {
  const { t } = useTranslation();
  const labelRef = useRef<HTMLElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const checkTruncate = () => {
      const el = labelRef.current;
      if (!el) {
        return;
      }

      setIsTruncated(el.scrollWidth > el.clientWidth);
    };

    checkTruncate();

    window.addEventListener("resize", checkTruncate);

    return () => {
      window.removeEventListener("resize", checkTruncate);
    };
  }, [label]);

  const labelText = t(label);

  const content = (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexGrow: 1, minWidth: 0 }}>
      <ListItemText
        primary={
          <Box
            component="span"
            ref={labelRef}
            sx={{ display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
          >
            {labelText}
          </Box>
        }
        sx={{ minWidth: 0 }}
      />

      {count ? (
        <Badge
          badgeContent={<Typography variant="caption">{count}</Typography>}
          sx={{
            "& .MuiBadge-badge": {
              fontSize: 12,
              minWidth: 24,
              height: 24,
              borderRadius: "50%",
              backgroundColor: "fsw.oragen-wcag",
              color: "white",
            },
          }}
        />
      ) : null}
    </Box>
  );

  // The full label is only reachable by mouse/keyboard users via this tooltip when the text is
  // actually cut off; screen readers get it from the ListItemButton's aria-label either way.
  return isTruncated ? (
    <Tooltip title={labelText} placement="right" arrow>
      {content}
    </Tooltip>
  ) : (
    content
  );
}
